import type { CSSProperties, ForwardedRef, ReactNode } from "react";
import React from "react";
import { getFocusStyle } from "../../theme";
import { css } from "../../theme/styled";
import { joinClassNames } from "../../util/util";

interface Props {
  children: ReactNode;
  element?: "div" | "span";
  onClick: (
    e: React.KeyboardEvent<Element> | React.MouseEvent<Element, MouseEvent>,
  ) => unknown;
  style?: CSSProperties;
  className?: string;
  tabIndex?: number;
  dataTestId?: string;
  dataComponentType?: string;
  /** role is required to describe the element type. List of values allowed https://www.w3.org/TR/html-aria/#allowed-aria-roles-states-and-properties */
  role: string;

  /**
   * wai-aria
   */
  /** Text to be read by screen readers */
  ariaLabel?: string;
  /** Sets the reference to the element that's being controlled from the FocusWrapper. Used to quickly navigate. */
  ariaControls?: string;
  /** Indication if the controlled group is expanded or collapsed. */
  ariaExpanded?: boolean;
}

export const FocusWrapper = React.forwardRef(
  (
    {
      children,
      className,
      element = "div",
      onClick,
      style,
      tabIndex = 0,
      dataComponentType,
      dataTestId,
      role,
      ariaLabel,
      ariaControls,
      ariaExpanded,
    }: Props,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const handleKeyDown = (e: React.KeyboardEvent<Element>): void => {
      if (e.key === "Enter" || e.key === " ") {
        onClick(e);
      }
    };
    const Wrapper = element;

    return (
      <Wrapper
        className={joinClassNames([wrapperStyles, className])}
        style={style}
        onKeyDown={(e: React.KeyboardEvent<Element>) => handleKeyDown(e)}
        onClick={(e: React.MouseEvent<Element>) => onClick(e)}
        tabIndex={tabIndex}
        data-test-id={dataTestId}
        data-component-type={dataComponentType || "FocusWrapper"}
        role={role}
        aria-label={ariaLabel}
        aria-controls={ariaControls}
        aria-expanded={ariaExpanded}
        ref={ref}
      >
        {children}
      </Wrapper>
    );
  },
);

const wrapperStyles = css({
  outline: "none",
  cursor: "pointer",
  ...getFocusStyle(),
});
